<template>
  <div v-if="isPecsmartAdmin && configured" class="ob-card" :class="statusClass">
    <div class="ob-card__header">
      <span class="ob-card__dot"></span>
      <span class="ob-card__title">Open Balena<template v-if="label"> — {{ label }}</template></span>
      <span class="ob-card__status">{{ statusLabel }}</span>
    </div>

    <div v-if="loading" class="ob-card__body">Buscando dispositivo...</div>

    <div v-else-if="!found" class="ob-card__body">
      Nenhum dispositivo correspondente encontrado no Open Balena.
    </div>

    <div v-else class="ob-card__body">
      <div class="ob-card__row" v-if="device.name">
        <strong>Dispositivo:</strong> {{ device.name }}
      </div>

      <div class="ob-card__row" v-if="statusDurationLabel">
        <strong>{{ statusLabel }}</strong> {{ statusDurationLabel }}
      </div>

      <div class="ob-card__actions">
        <a
          :href="device.adminUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="ob-card__btn ob-card__btn--link"
        >
          Abrir no Open Balena
        </a>

        <button
          type="button"
          class="ob-card__btn ob-card__btn--danger"
          :disabled="!canReboot || rebooting || cooldownActive"
          @click="askReboot"
        >
          {{ rebootButtonLabel }}
        </button>

        <button type="button" class="ob-card__btn" @click="toggleLogs">
          {{ expanded ? "▲ Ocultar logs" : "▼ Ver logs" }}
        </button>
      </div>

      <div v-if="feedback" class="ob-card__feedback" :class="{ 'ob-card__feedback--error': !feedback.success }">
        {{ feedback.message }}
      </div>

      <div v-if="expanded" class="ob-card__logs-panel">
        <div class="ob-card__logs-header">
          <span>Logs do dispositivo</span>
          <button type="button" class="ob-card__btn ob-card__btn--small" :disabled="logsLoading" @click="loadLogs">
            {{ logsLoading ? "Atualizando..." : "Atualizar" }}
          </button>
        </div>

        <div v-if="logsLoading && logs.length === 0" class="ob-card__logs-empty">Carregando logs...</div>

        <div v-else-if="logsError" class="ob-card__logs-empty ob-card__logs-empty--error">
          {{ logsError }}
        </div>

        <div v-else-if="logs.length === 0" class="ob-card__logs-empty">Nenhum log disponível.</div>

        <pre v-else class="ob-card__logs"><div
          v-for="(line, index) in logs"
          :key="index"
          class="ob-card__log-line"
          :class="{ 'ob-card__log-line--stderr': line.isStdErr }"
        ><span v-if="line.timestamp" class="tok-time">{{ formatLogTimestamp(line.timestamp) }}</span> <span v-html="highlightLogMessage(line.message)"></span></div></pre>
      </div>
    </div>

    <div
      v-if="confirmOpen"
      class="ob-card__confirm-overlay"
      @click.self="confirmOpen = false"
    >
      <div class="ob-card__confirm">
        <p>
          Deseja reiniciar {{ device.name || "este sensor" }}? O sensor ficará offline
          temporariamente e voltará em alguns minutos.
        </p>
        <div class="ob-card__confirm-actions">
          <button type="button" class="ob-card__btn" @click="confirmOpen = false">Cancelar</button>
          <button type="button" class="ob-card__btn ob-card__btn--danger" @click="handleRebootConfirm">
            Reiniciar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  isOpenBalenaConfigured,
  lookupOpenBalenaDevice,
  refreshOpenBalenaDeviceStatus,
  getOpenBalenaConnectivity,
  canRebootOpenBalenaDevice,
  rebootOpenBalenaDevice,
  fetchOpenBalenaDeviceLogs,
} from "../utils/openBalena";
import { formatDistanceToNow, format as formatDate } from "date-fns";
import { ptBR } from "date-fns/locale";

const STATUS_REFRESH_INTERVAL_MS = 15000;
const REBOOT_COOLDOWN_MS = 3 * 60 * 1000;
const POLL_INTERVAL_MS = 15000;
const POLL_MAX_ATTEMPTS = 20;
const LOGS_REFRESH_INTERVAL_MS = 7000;

// Highlight de log em um único passe (evita re-casar dentro das tags já
// inseridas por um passe anterior). Ordem das alternativas = prioridade.
const LOG_TOKEN_REGEX =
  /(&quot;.*?&quot;|&#39;.*?&#39;)|\b(FATAL|ERROR|ERR)\b|\b(WARN|WARNING)\b|\b(INFO|SUCCESS|OK)\b|\b(DEBUG|TRACE)\b|\b([a-zA-Z_][\w.-]*)(?=[:=]\S)|\b(\d+(?:\.\d+)?)\b/gi;

// Muitos logs do supervisor já vêm coloridos via códigos ANSI SGR
// (ex.: ESC[36mtexto ESC[0m) — interpretamos essas cores em vez de
// aplicar nosso highlight genérico por cima do texto literal do escape.
// eslint-disable-next-line no-control-regex
const ANSI_ESCAPE_REGEX = /\x1b\[([0-9;]*)m/g;
// eslint-disable-next-line no-control-regex
const ANSI_ESCAPE_TEST_REGEX = /\x1b\[[0-9;]*m/;
const ANSI_FG_COLORS = {
  30: "#6a6a6a",
  31: "#f14c4c",
  32: "#6a9955",
  33: "#f5a623",
  34: "#4fc1ff",
  35: "#c586c0",
  36: "#4ec9b0",
  37: "#d4d4d4",
  90: "#808080",
  91: "#ff6e6e",
  92: "#8fd67a",
  93: "#ffd479",
  94: "#7cc4ff",
  95: "#e0a6e8",
  96: "#7fe0d0",
  97: "#ffffff",
};

export default {
  name: "OpenBalenaCard",
  props: {
    sensor: {
      type: Object,
      required: true,
    },
    label: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      configured: isOpenBalenaConfigured(),
      loading: true,
      found: false,
      device: null,
      rebooting: false,
      cooldownActive: false,
      confirmOpen: false,
      feedback: null,
      refreshTimer: null,
      cooldownTimer: null,
      pollTimer: null,
      expanded: false,
      logs: [],
      logsLoading: false,
      logsError: null,
      logsRefreshTimer: null,
    };
  },
  computed: {
    isPecsmartAdmin() {
      return this.$store.getters.getCurrentUser?.role === "pecsmart_admin";
    },
    connectivity() {
      return this.found ? getOpenBalenaConnectivity(this.device) : "offline";
    },
    statusClass() {
      return `ob-card--${this.connectivity}`;
    },
    statusLabel() {
      if (!this.found) return "Não encontrado";
      if (this.connectivity === "offline") return "Offline";
      if (this.connectivity === "warning") return "Online (VPN desconectada)";
      return "Online";
    },
    canReboot() {
      return this.found && canRebootOpenBalenaDevice(this.device);
    },
    rebootButtonLabel() {
      if (this.rebooting) return "Reiniciando...";
      if (this.cooldownActive) return "Aguarde...";
      return "Reiniciar sensor";
    },
    statusDurationLabel() {
      if (!this.found || !this.device) return null;
      // "last connectivity event" marca quando o dispositivo mudou de
      // estado (ficou online ou offline) pela última vez.
      const raw = this.device.lastConnectivityEvent || this.device.lastVpnEvent;
      if (!raw) return null;
      const date = new Date(raw);
      if (Number.isNaN(date.getTime())) return null;
      return formatDistanceToNow(date, { locale: ptBR, addSuffix: true });
    },
  },
  watch: {
    sensor: {
      immediate: true,
      handler() {
        this.loadDevice();
      },
    },
  },
  beforeDestroy() {
    this.stopRefreshLoop();
    this.stopPolling();
    this.stopLogsRefreshLoop();
    if (this.cooldownTimer) clearTimeout(this.cooldownTimer);
  },
  methods: {
    async loadDevice() {
      if (!this.isPecsmartAdmin || !this.configured) return;
      this.loading = true;
      this.expanded = false;
      this.logs = [];
      this.logsError = null;
      this.stopLogsRefreshLoop();
      const result = await lookupOpenBalenaDevice(this.sensor, this.$store.getters.getHeader);
      this.found = result.found;
      this.device = result.device;
      this.loading = false;
      this.startRefreshLoop();
    },
    startRefreshLoop() {
      this.stopRefreshLoop();
      if (!this.found) return;
      this.refreshTimer = setInterval(async () => {
        this.device = await refreshOpenBalenaDeviceStatus(this.device);
      }, STATUS_REFRESH_INTERVAL_MS);
    },
    stopRefreshLoop() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    askReboot() {
      if (!this.canReboot || this.rebooting || this.cooldownActive) return;
      this.confirmOpen = true;
    },
    async handleRebootConfirm() {
      this.confirmOpen = false;
      this.rebooting = true;
      this.feedback = null;

      const result = await rebootOpenBalenaDevice(this.device);
      this.feedback = result;
      this.rebooting = false;

      if (result.success) {
        this.startCooldown();
        this.pollStatusAfterReboot();
      }
    },
    startCooldown() {
      this.cooldownActive = true;
      this.cooldownTimer = setTimeout(() => {
        this.cooldownActive = false;
      }, REBOOT_COOLDOWN_MS);
    },
    pollStatusAfterReboot() {
      this.stopPolling();
      let attempts = 0;
      this.pollTimer = setInterval(async () => {
        attempts += 1;
        this.device = await refreshOpenBalenaDeviceStatus(this.device);
        const recovered = getOpenBalenaConnectivity(this.device) === "healthy";
        if (recovered || attempts >= POLL_MAX_ATTEMPTS) {
          this.stopPolling();
        }
      }, POLL_INTERVAL_MS);
    },
    toggleLogs() {
      this.expanded = !this.expanded;
      if (this.expanded) {
        if (this.logs.length === 0 && !this.logsLoading) {
          this.loadLogs();
        }
        this.startLogsRefreshLoop();
      } else {
        this.stopLogsRefreshLoop();
      }
    },
    startLogsRefreshLoop() {
      this.stopLogsRefreshLoop();
      this.logsRefreshTimer = setInterval(() => {
        this.loadLogs();
      }, LOGS_REFRESH_INTERVAL_MS);
    },
    stopLogsRefreshLoop() {
      if (this.logsRefreshTimer) {
        clearInterval(this.logsRefreshTimer);
        this.logsRefreshTimer = null;
      }
    },
    async loadLogs() {
      if (!this.found || !this.device) return;
      this.logsLoading = true;
      this.logsError = null;
      try {
        const entries = await fetchOpenBalenaDeviceLogs(this.device);
        // Mais recente primeiro.
        this.logs = entries.slice().sort((a, b) => {
          const at = a.timestamp ? a.timestamp.getTime() : 0;
          const bt = b.timestamp ? b.timestamp.getTime() : 0;
          return bt - at;
        });
      } catch (e) {
        this.logsError = "Não foi possível carregar os logs do dispositivo.";
      } finally {
        this.logsLoading = false;
      }
    },
    formatLogTimestamp(date) {
      return formatDate(date, "dd/MM HH:mm:ss", { locale: ptBR });
    },
    escapeHtml(str) {
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    highlightLogMessage(message) {
      const raw = message || "";
      // O supervisor já envia os logs coloridos via códigos ANSI (ex.:
      // ESC[36m...ESC[0m) — interpretamos esses códigos em vez de
      // exibi-los como texto literal.
      if (ANSI_ESCAPE_TEST_REGEX.test(raw)) {
        return this.ansiToHtml(raw);
      }
      return this.highlightPlainMessage(raw);
    },
    ansiToHtml(text) {
      const escaped = this.escapeHtml(text);
      let result = "";
      let lastIndex = 0;
      let openSpan = false;
      let color = null;
      let bold = false;

      const closeSpan = () => {
        if (openSpan) {
          result += "</span>";
          openSpan = false;
        }
      };
      const openSpanIfStyled = () => {
        if (!color && !bold) return;
        const styles = [];
        if (color) styles.push(`color:${color}`);
        if (bold) styles.push("font-weight:600");
        result += `<span style="${styles.join(";")}">`;
        openSpan = true;
      };

      ANSI_ESCAPE_REGEX.lastIndex = 0;
      let match;
      while ((match = ANSI_ESCAPE_REGEX.exec(escaped)) !== null) {
        result += escaped.slice(lastIndex, match.index);
        lastIndex = ANSI_ESCAPE_REGEX.lastIndex;
        closeSpan();

        const codes = match[1].split(";").filter(Boolean).map(Number);
        if (codes.length === 0) codes.push(0);
        codes.forEach((code) => {
          if (code === 0) {
            color = null;
            bold = false;
          } else if (code === 1) {
            bold = true;
          } else if (ANSI_FG_COLORS[code]) {
            color = ANSI_FG_COLORS[code];
          }
        });
        openSpanIfStyled();
      }
      result += escaped.slice(lastIndex);
      closeSpan();
      return result;
    },
    highlightPlainMessage(message) {
      const escaped = this.escapeHtml(message);
      return escaped.replace(
        LOG_TOKEN_REGEX,
        (match, str, fatal, warn, info, debug, key, num) => {
          if (str) return `<span class="tok-string">${str}</span>`;
          if (fatal) return `<span class="tok-error">${fatal}</span>`;
          if (warn) return `<span class="tok-warn">${warn}</span>`;
          if (info) return `<span class="tok-info">${info}</span>`;
          if (debug) return `<span class="tok-debug">${debug}</span>`;
          if (key) return `<span class="tok-key">${key}</span>`;
          if (num) return `<span class="tok-number">${num}</span>`;
          return match;
        }
      );
    },
  },
};
</script>

<style scoped>
.ob-card {
  border-radius: 8px;
  border: 1px solid #d9d9d9;
  padding: 12px 16px;
  margin: 12px 0;
  background: #f7f7f7;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.ob-card--healthy {
  background: #eafaf1;
  border-color: #34c759;
}

.ob-card--warning {
  background: #fff8e6;
  border-color: #f5a623;
}

.ob-card--offline {
  background: #fdecec;
  border-color: #e74c3c;
}

.ob-card__header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.ob-card__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: currentColor;
}

.ob-card--healthy .ob-card__dot {
  background: #34c759;
}
.ob-card--warning .ob-card__dot {
  background: #f5a623;
}
.ob-card--offline .ob-card__dot {
  background: #e74c3c;
}

.ob-card__status {
  margin-left: auto;
  font-weight: 500;
  font-size: 0.85rem;
}

.ob-card__body {
  margin-top: 8px;
  font-size: 0.9rem;
}

.ob-card__row {
  margin-bottom: 6px;
}

.ob-card__actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.ob-card__btn {
  border: 1px solid #ccc;
  background: #fff;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 0.85rem;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
}

.ob-card__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ob-card__btn--danger {
  background: #e74c3c;
  border-color: #e74c3c;
  color: #fff;
}

.ob-card__feedback {
  margin-top: 8px;
  font-size: 0.85rem;
  color: #1a7f37;
}

.ob-card__feedback--error {
  color: #c0392b;
}

.ob-card__btn--small {
  padding: 3px 8px;
  font-size: 0.78rem;
}

.ob-card__logs-panel {
  margin-top: 10px;
  border: 1px solid #333;
  border-radius: 6px;
  overflow: hidden;
  background: #1e1e1e;
}

.ob-card__logs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: #2a2a2a;
  color: #ddd;
  font-size: 0.8rem;
  font-weight: 600;
}

.ob-card__logs-empty {
  padding: 12px;
  color: #aaa;
  font-size: 0.85rem;
}

.ob-card__logs-empty--error {
  color: #ff8a80;
}

.ob-card__logs {
  margin: 0;
  padding: 8px 10px;
  max-height: 320px;
  overflow-y: auto;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 0.78rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  color: #d4d4d4;
}

.ob-card__log-line {
  border-left: 2px solid transparent;
  padding-left: 6px;
}

.ob-card__log-line--stderr {
  border-left-color: #e74c3c;
  background: rgba(231, 76, 60, 0.08);
}

.tok-time {
  color: #6a9955;
}

.tok-error {
  color: #f14c4c;
  font-weight: 600;
}

.tok-warn {
  color: #f5a623;
  font-weight: 600;
}

.tok-info {
  color: #4fc1ff;
}

.tok-debug {
  color: #9cdcfe;
}

.tok-string {
  color: #ce9178;
}

.tok-key {
  color: #9cdcfe;
}

.tok-number {
  color: #b5cea8;
}

.ob-card__confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.ob-card__confirm {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  max-width: 360px;
}

.ob-card__confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
</style>
